import React, { useEffect, useRef, useState, useCallback } from "react";
import WaveSurfer from "wavesurfer.js";

const activeWaveSurferRef = { current: null };

const WaveformAudioPlayer = ({ src, className = "" }) => {
  const containerRef = useRef(null);
  const waveSurferRef = useRef(null);
  const isCreatingRef = useRef(false);

  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const createWaveSurfer = useCallback((autoPlay = false) => {
    if (!containerRef.current || waveSurferRef.current || !src || isCreatingRef.current) return;
    isCreatingRef.current = true;

    requestAnimationFrame(() => {
       if (!containerRef.current || waveSurferRef.current || !src) {
        isCreatingRef.current = false;
        return;
      }
      const ws = WaveSurfer.create({
        container: containerRef.current,
        height: 50,
        waveColor: "#ccc",
        progressColor: "#2b7cff",
        cursorWidth: 0,
        barWidth: 2,
        barRadius: 2,
        responsive: true,
      });

      ws.load(src);

      ws.on("ready", () => {
        ws.setOptions({ width: containerRef.current.offsetWidth });
        setIsReady(true);
        setDuration(ws.getDuration());

        // 🔥 Pause previous audio when new one is ready to play
        // if (activeWaveSurferRef.current && activeWaveSurferRef.current !== ws) {
        //   activeWaveSurferRef.current.pause();
        //   activeWaveSurferRef.current.seekTo(0);
        //   activeWaveSurferRef.current = null;
        // }

        // ws.play();
        // activeWaveSurferRef.current = ws;

        if (autoPlay) {
          // 🔥 Pause previous audio when new one is ready to play
          if (activeWaveSurferRef.current && activeWaveSurferRef.current !== ws) {
            activeWaveSurferRef.current.pause();
            activeWaveSurferRef.current.seekTo(0);
            activeWaveSurferRef.current = null;
          }

          ws.play();
          activeWaveSurferRef.current = ws;
        }
        isCreatingRef.current = false;
      });

      ws.on("play", () => setIsPlaying(true));

      ws.on("pause", () => {
        setIsPlaying(false);
        if (activeWaveSurferRef.current === ws) {
          activeWaveSurferRef.current = null;
        }
      });

      ws.on("finish", () => {
        setIsPlaying(false);
        setCurrentTime(ws.getDuration());
        if (activeWaveSurferRef.current === ws) {
          activeWaveSurferRef.current = null;
        }
      });

       ws.on("audioprocess", () => {
        setCurrentTime(ws.getCurrentTime());
      });

      ws.on("seek", () => {
        setCurrentTime(ws.getCurrentTime());
      });
      
      ws.on("error", () => {
        isCreatingRef.current = false;
      });
      
      waveSurferRef.current = ws;
      setIsInitialized(true);
    });
  }, [src , duration]);

  useEffect(() => {
    createWaveSurfer(false); // false = don't auto-play
  }, [createWaveSurfer]);

  const handlePlayPause = () => {
    // First time clicking — initialize and auto-play (ready handler takes care of pausing others)
    if (!isInitialized) {
      createWaveSurfer(true);
      return;
    }

    const ws = waveSurferRef.current;
    if (!ws || !isReady) return;

    const isCurrentlyPlaying = ws.isPlaying();

    if (isCurrentlyPlaying) {
      // Pause current
      ws.pause();
      activeWaveSurferRef.current = null;
    } else {
      // 🔥 Pause any other playing audio before resuming this one
      if (activeWaveSurferRef.current && activeWaveSurferRef.current !== ws) {
        activeWaveSurferRef.current.pause();
        activeWaveSurferRef.current.seekTo(0);
        activeWaveSurferRef.current = null;
      }

      ws.play();
      activeWaveSurferRef.current = ws;
    }
  };

  useEffect(() => {
    return () => {
      const ws = waveSurferRef.current;
      if (ws) {
        if (activeWaveSurferRef.current === ws) {
          activeWaveSurferRef.current = null;
        }
        ws.destroy();
        waveSurferRef.current = null;
      }
    };
  }, []);

   const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!src) return null;

  return (
    <div
      className={`waveform-player d-flex align-items-center gap-3 ${className}`}
      style={{ width: "100%" }}
    >
      <div style={{ flex: "0 0 auto" }}>
        <button type="button" aria-label={isPlaying ? "Pause audio" : "Play audio"} onClick={handlePlayPause} className="neon-green-btn">
          {isPlaying ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#ffffff">
              <rect x="6" y="6" width="12" height="12" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#ffffff">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>
      </div>

      <div style={{ flex: 1, minWidth: 0, maxWidth: '60%' }}>
        <div
          className="waveform-container"
          ref={containerRef}
          style={{ width: "100%", height: "50px" }}
        >
          {!isInitialized && (
            <div className="fake-wave">
              {Array.from({ length: 100 }).map((_, i) => (
                <span key={i} />
              ))}
            </div>
          )}
        </div>
      </div>

      {isReady && duration > 0 && (
        <div style={{ flex: "0 0 auto", minWidth: "80px", textAlign: "right" }}>
          <small className="text-muted audio-duration-time">
            <span style={{ color: isPlaying ? "#2b7cff" : "#666", fontWeight: isPlaying ? "600" : "normal" }}>
              {formatTime(currentTime)}
            </span>
            {" / "}
            {formatTime(duration)}
          </small>
        </div>
      )}
    </div>
  );
};

export default React.memo(WaveformAudioPlayer);